import React, { useState, useEffect } from 'react';
import { ChevronLeft, Shield, BarChart3 } from 'lucide-react';
import { cookiePreferencesService } from '../../../services/settings/cookiePreferencesService';

/**
 * CONSENT_VERSION: Bei strukturellen Änderungen (z.B. neue Cookie-Kategorie,
 * neuer Tracking-Anbieter, neue Speicherdauern) hochzählen. Existierende Consents
 * werden bei Versionswechsel ungültig und der Banner erscheint erneut.
 *
 * 1: Initial (essential, analytics, functional)
 */
const CONSENT_VERSION = 1;

/**
 * Maximale Gültigkeit einer Einwilligung (in Millisekunden).
 * Aufsichtsbehörden empfehlen Refresh nach 12 Monaten.
 */
const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

interface CookieSettings {
  essential: boolean;
  /** Google Analytics */
  analytics: boolean;
  /** Funktionalität & Komfort (aktuell nicht aktiv genutzt -- für zukünftige Erweiterungen) */
  functional: boolean;
}

interface StoredConsent extends Partial<CookieSettings> {
  version?: number;
  timestamp?: number;
}

export const CookieBannerTW: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [settings, setSettings] = useState<CookieSettings>({
    essential: true,
    analytics: false,
    functional: false,
  });

  // Scroll-Lock: Body-Scroll und Lenis sperren wenn Modal offen ist
  useEffect(() => {
    const lenis = (window as any).__lenis;
    if (showBanner) {
      document.body.style.overflow = 'hidden';
      lenis?.stop();
    } else {
      document.body.style.overflow = '';
      lenis?.start();
    }

    return () => {
      document.body.style.overflow = '';
      const lenisInstance = (window as any).__lenis;
      lenisInstance?.start();
    };
  }, [showBanner]);

  useEffect(() => {
    const isConsentStillValid = (stored: StoredConsent): boolean => {
      if (stored.version !== CONSENT_VERSION) return false;
      if (!stored.timestamp) return false;
      if (Date.now() - stored.timestamp > CONSENT_MAX_AGE_MS) return false;
      return true;
    };

    const initializeCookieConsent = async () => {
      // Check if user is logged in
      const token = localStorage.getItem('token');

      if (token) {
        // Logged in user: sync with database
        const dbPreferences = await cookiePreferencesService.syncWithDatabase();
        if (dbPreferences) {
          const resolved: CookieSettings = {
            essential: true,
            analytics: dbPreferences.analytics || false,
            functional: dbPreferences.functional || false,
          };
          setSettings(resolved);
          // Bestehende Einwilligung direkt anwenden
          if (resolved.analytics && (window as any).GA_MEASUREMENT_ID) {
            loadGoogleAnalytics();
          }
          // Don't show banner if user has already consented
          return;
        }
      }

      // Not logged in or no database preferences: check localStorage
      const consent = localStorage.getItem('cookieConsent');
      if (!consent) {
        // Small delay to ensure smooth animation
        setTimeout(() => setShowBanner(true), 100);
      } else {
        // Load saved settings
        try {
          const savedSettings: StoredConsent = JSON.parse(consent);

          // Wenn Version oder Alter nicht mehr gültig: Banner erneut zeigen
          if (!isConsentStillValid(savedSettings)) {
            localStorage.removeItem('cookieConsent');
            localStorage.removeItem('cookieConsentTimestamp');
            setTimeout(() => setShowBanner(true), 100);
            return;
          }

          const resolved: CookieSettings = {
            essential: true,
            analytics: savedSettings.analytics ?? false,
            functional: savedSettings.functional ?? false,
          };
          setSettings(resolved);
          // Bestehende Einwilligung direkt anwenden
          if (resolved.analytics && (window as any).GA_MEASUREMENT_ID) {
            loadGoogleAnalytics();
          }
        } catch (e) {
          console.error('Failed to parse saved cookie settings:', e);
          // Korrupte Daten: lieber neu fragen
          localStorage.removeItem('cookieConsent');
          setTimeout(() => setShowBanner(true), 100);
        }
      }
    };

    initializeCookieConsent();

    // Listen for event to open cookie settings
    const handleOpenCookieSettings = () => {
      // Load current settings from localStorage when reopening
      const savedConsent = localStorage.getItem('cookieConsent');
      if (savedConsent) {
        try {
          const savedSettings: StoredConsent = JSON.parse(savedConsent);
          setSettings({
            essential: true,
            analytics: savedSettings.analytics ?? false,
            functional: savedSettings.functional ?? false,
          });
        } catch (e) {
          console.error('Failed to parse saved cookie settings:', e);
        }
      }
      setShowSettings(true);
      setShowBanner(true);
    };

    window.addEventListener('openCookieSettings', handleOpenCookieSettings);

    return () => {
      window.removeEventListener('openCookieSettings', handleOpenCookieSettings);
    };
  }, []);

  const handleAcceptAll = () => {
    const fullConsent: CookieSettings = {
      essential: true,
      analytics: true,
      functional: true,
    };
    saveConsent(fullConsent, 'all');
  };

  const handleRejectAll = () => {
    const minimalConsent: CookieSettings = {
      essential: true,
      analytics: false,
      functional: false,
    };
    saveConsent(minimalConsent, 'necessary');
  };

  const handleSaveSettings = () => {
    saveConsent(settings, 'custom');
  };

  const saveConsent = async (consentSettings: CookieSettings, type: string) => {
    const now = Date.now();
    const stored: StoredConsent = {
      ...consentSettings,
      version: CONSENT_VERSION,
      timestamp: now,
    };

    // Save to localStorage first (mit Version + Timestamp für Audit/Refresh)
    localStorage.setItem('cookieConsent', JSON.stringify(stored));
    localStorage.setItem('cookieConsentTimestamp', String(now));

    // If user is logged in, also save to database
    const token = localStorage.getItem('token');
    if (token) {
      await cookiePreferencesService.savePreferences({
        analytics: consentSettings.analytics,
        functional: consentSettings.functional,
      });
    }

    // Google Analytics: bei Einwilligung laden, bei Widerruf deaktivieren + Cookies löschen
    if (consentSettings.analytics && (window as any).GA_MEASUREMENT_ID) {
      loadGoogleAnalytics();
    } else if (!consentSettings.analytics) {
      disableGoogleAnalytics();
    }

    setShowBanner(false);
    setShowSettings(false);
    showSuccessNotification(type);

    // Dispatch event for other components
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', {
      detail: consentSettings
    }));
  };

  /**
   * Lädt das Google-Analytics-Script. Wird AUSSCHLIESSLICH aufgerufen,
   * nachdem der Nutzer Analytics aktiv eingewilligt hat (§ 25 TDDDG).
   */
  const loadGoogleAnalytics = () => {
    // Idempotent: schon geladen? Nur Consent-Update reicht (z.B. nach Re-Aktivierung)
    if ((window as any).googleAnalyticsLoaded) {
      const gtagExisting = (window as any).gtag;
      if (typeof gtagExisting === 'function') {
        gtagExisting('consent', 'update', {
          analytics_storage: 'granted',
          ad_storage: 'denied',
        });
      }
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${(window as any).GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    script.onload = () => {
      (window as any).dataLayer = (window as any).dataLayer || [];
      function gtag(...args: any[]) {
        (window as any).dataLayer.push(args);
      }
      (window as any).gtag = gtag;

      // Default: alles verboten -- erst dann erlauben, was eingewilligt wurde.
      gtag('consent', 'default', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
      });
      gtag('consent', 'update', {
        analytics_storage: 'granted',
        ad_storage: 'denied',
      });
      gtag('js', new Date());
      gtag('config', (window as any).GA_MEASUREMENT_ID, {
        'cookie_flags': 'SameSite=None;Secure'
      });
      (window as any).googleAnalyticsLoaded = true;
    };
  };

  /**
   * Bei Widerruf der Analytics-Einwilligung: gtag-Consent auf "denied"
   * setzen (verhindert weitere Tracking-Events) und alle bekannten
   * Google-Analytics-Cookies löschen.
   */
  const disableGoogleAnalytics = () => {
    const gtag = (window as any).gtag;
    if (typeof gtag === 'function') {
      gtag('consent', 'update', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
      });
    }

    // Bekannte GA-Cookies entfernen (_ga, _gid, _gat_*, _ga_*)
    const cookies = document.cookie.split(';');
    for (const raw of cookies) {
      const eqIndex = raw.indexOf('=');
      const name = (eqIndex > -1 ? raw.substring(0, eqIndex) : raw).trim();
      if (
        name === '_ga' ||
        name === '_gid' ||
        name.startsWith('_gat') ||
        name.startsWith('_ga_')
      ) {
        // Auf aktueller Domain und auf .renditefuchs.de löschen
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}`;
      }
    }
  };

  const showSuccessNotification = (_type: string) => {
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/70 backdrop-blur-sm z-[10000] flex items-end justify-center p-4 sm:p-6 transition-opacity duration-300 ${showBanner ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        {/* Modal */}
        <div
          className={`w-full max-w-xl max-h-[80vh] bg-gray-900 rounded-xl border border-white/10 shadow-2xl flex flex-col overflow-hidden transition-transform duration-400 ${showBanner ? 'translate-y-0' : 'translate-y-full'}`}
        >
          {!showSettings ? (
            // Main View
            <>
              {/* Header */}
              <div className="px-6 pt-6 pb-4 border-b border-white/10">
                <h2 className="text-xl font-semibold text-gray-100 mb-2">
                  Cookie-Einstellungen
                </h2>
                <p className="text-sm text-gray-400">
                  Wir verwenden Cookies, um Ihre Erfahrung auf RENDITEFUCHS zu verbessern.
                </p>
              </div>

              {/* Body */}
              <div className="px-6 py-4 flex-1 overflow-y-auto scrollbar-styled" data-lenis-prevent>
                <p className="text-sm text-gray-300 leading-relaxed mb-3">
                  Wir verwenden technisch notwendige Cookies, damit die Website funktioniert.
                  Optional setzen wir mit Ihrer Einwilligung Analyse-Cookies ein, um die Nutzung
                  unserer Website besser zu verstehen und unser Angebot zu verbessern.
                </p>
                <p className="text-sm text-gray-300 leading-relaxed">
                  Sie können selbst entscheiden, ob Sie die Analyse-Cookies aktivieren möchten,
                  und Ihre Einstellungen jederzeit über den Link "Cookie-Einstellungen" im
                  Footer anpassen. Weitere Informationen finden Sie in unserer{' '}
                  <a href="/datenschutz" className="text-brand hover:text-brand-hover transition-colors">
                    Datenschutzerklärung
                  </a>.
                </p>
              </div>

              {/* Actions
                  TDDDG-konform: "Alle akzeptieren" und "Nur notwendige" sind gleichwertig
                  gestaltet (gleiche Größe, gleiche Schrift, vergleichbare Farbe).
                  "Einstellungen" als sekundärer Button. */}
              <div className="px-6 py-4 border-t border-white/10 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 px-4 py-2.5 bg-brand text-gray-900 font-semibold rounded-lg hover:bg-brand-hover transition-colors"
                >
                  Alle akzeptieren
                </button>
                <button
                  onClick={handleRejectAll}
                  className="flex-1 px-4 py-2.5 bg-gray-700 text-gray-100 font-semibold rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Nur notwendige
                </button>
                <button
                  onClick={() => setShowSettings(true)}
                  className="flex-1 px-4 py-2.5 bg-transparent text-gray-300 font-medium rounded-lg border border-white/10 hover:bg-gray-800 hover:text-white transition-colors"
                >
                  Einstellungen
                </button>
              </div>
            </>
          ) : (
            // Settings View
            <>
              {/* Header */}
              <div className="px-6 pt-6 pb-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowSettings(false)}
                    className="p-2 -ml-2 text-gray-400 hover:text-brand rounded-lg hover:bg-white/5 transition-colors"
                    aria-label="Zurück"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <h2 className="text-lg font-semibold text-gray-100">
                    Cookie-Einstellungen verwalten
                  </h2>
                </div>
              </div>

              {/* Body */}
              <div className="px-6 py-4 flex-1 overflow-y-auto scrollbar-styled space-y-4" data-lenis-prevent>
                {/* Essential Cookies */}
                <div className="p-4 bg-gray-800/50 rounded-lg border border-white/5">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-status-success/10 rounded-lg">
                        <Shield className="w-5 h-5 text-status-success" />
                      </div>
                      <span className="font-medium text-gray-100">Notwendige Cookies</span>
                    </div>
                    <ToggleSwitch checked={true} disabled onChange={() => {}} />
                  </div>
                  <p className="text-sm text-gray-400 mb-3">
                    Diese Cookies sind für das Funktionieren der Website unerlässlich und können nicht deaktiviert werden.
                  </p>
                  <div className="text-xs text-gray-500 bg-black/20 rounded-md p-3 space-y-1">
                    <p><strong className="text-gray-400">Verwendete Cookies:</strong></p>
                    <p>Session-Cookies, Cookie-Präferenzen, Sicherheits-Tokens</p>
                    <p><strong className="text-gray-400">Speicherdauer:</strong> Session oder max. 365 Tage</p>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="p-4 bg-gray-800/50 rounded-lg border border-white/5">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-status-info/10 rounded-lg">
                        <BarChart3 className="w-5 h-5 text-status-info" />
                      </div>
                      <span className="font-medium text-gray-100">Analyse & Performance</span>
                    </div>
                    <ToggleSwitch
                      checked={settings.analytics}
                      onChange={(checked) => setSettings({...settings, analytics: checked})}
                    />
                  </div>
                  <p className="text-sm text-gray-400 mb-3">
                    Wir nutzen Google Analytics, um zu verstehen, wie Sie unsere Website nutzen. Ihre IP-Adresse wird anonymisiert.
                  </p>
                  <div className="text-xs text-gray-500 bg-black/20 rounded-md p-3 space-y-1">
                    <p><strong className="text-gray-400">Dienst:</strong> Google Analytics mit IP-Anonymisierung</p>
                    <p><strong className="text-gray-400">Speicherdauer:</strong> 14 Monate</p>
                  </div>
                </div>

                {/*
                  Funktionale Cookies (Theme, Sprache) werden als technisch
                  notwendig eingestuft -- sie sind unmittelbar mit einer
                  Nutzeraktion verbunden und werden nicht zum Tracking
                  verwendet. Daher keine eigene Einwilligungs-Kategorie.
                */}
              </div>

              {/* Actions */}
              <div className="px-6 py-4 border-t border-white/10">
                <button
                  onClick={handleSaveSettings}
                  className="w-full px-4 py-2.5 bg-brand text-gray-900 font-semibold rounded-lg hover:bg-brand-hover transition-colors"
                >
                  Einstellungen speichern
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Success Message */}
      <div
        className={`fixed top-4 right-4 sm:top-6 sm:right-6 px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg shadow-lg z-[10002] transition-all duration-300 ${showSuccess ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full pointer-events-none'}`}
      >
        <p className="text-sm text-gray-100 font-medium">
          Cookie-Einstellungen wurden gespeichert
        </p>
      </div>
    </>
  );
};

// Toggle Switch Component
interface ToggleSwitchProps {
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ checked, disabled = false, onChange }) => {
  const handleClick = () => {
    if (!disabled) {
      onChange(!checked);
    }
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={handleClick}
      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-300 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900 ${
        disabled
          ? 'cursor-not-allowed bg-green-600'
          : checked
            ? 'bg-brand'
            : 'bg-gray-700'
      }`}
    >
      <span
        className="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-lg ring-0"
        style={{
          transform: `translateX(${checked || disabled ? '1rem' : '0'})`,
          transition: 'transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1)'
        }}
      />
    </button>
  );
};
